import { jsPDF } from 'jspdf';
import { buildCsv, downloadCsv, exportDateStamp } from '@/lib/csv';
import {
  formatAttendanceDay,
  formatDuration,
  formatShiftEnd,
  formatTimeOfDay,
  isOpenRecord,
} from '@/modules/attendance/hr-my-attendance/services/attendance-rules';
import {
  DAY_STATUS_LABEL_KEY,
  dayDisplayStatusOf,
} from '@/modules/attendance/hr-my-attendance/components/day-status-badge/day-status-badge';
import { AttendanceRecord, RosterRow } from '../types/manage-attendance.types';

/** One exported line: an employee's day (a roster row without a record has only the name). */
export interface AttendanceExportRow {
  employee: string;
  department: string;
  date: string;
  record: AttendanceRecord | null;
}

export const exportRowsOfRecords = (records: AttendanceRecord[]): AttendanceExportRow[] =>
  records.map((record) => ({
    employee: record.DisplayName ?? '',
    department: record.DepartmentName ?? '',
    date: record.AttendanceDate,
    record,
  }));

export const exportRowsOfRoster = (rows: RosterRow[], day: string): AttendanceExportRow[] =>
  rows.map((row) => ({
    employee: row.employee.DisplayName ?? '',
    department: row.employee.DepartmentName ?? row.record?.DepartmentName ?? '',
    date: `${day}T00:00:00.000Z`,
    record: row.record,
  }));

const HEADER_KEYS = [
  'EMPLOYEE_NAME',
  'DEPARTMENT',
  'ATTENDANCE_DATE',
  'IN_TIME',
  'OUT_TIME',
  'BREAK_TIME',
  'TOTAL_WORK_TIME',
  'EXPECTED_WORK_TIME',
  'OVERTIME',
  'STATUS',
] as const;

const duration = (value?: number | null) =>
  value == null ? '' : `${value < 0 ? '-' : ''}${formatDuration(Math.abs(value))}`;

/** The row's cells, in HEADER_KEYS order. Times in the record's own Timezone (timezone standard rule 6). */
const cellsOf = (row: AttendanceExportRow, t: (key: string) => string): string[] => {
  const r = row.record;
  const status = dayDisplayStatusOf(r);
  const statusText = [
    status ? t(DAY_STATUS_LABEL_KEY[status]) : '',
    r?.IsForceClockOut ? t('AUTO_CLOCKED_OUT') : '',
  ]
    .filter(Boolean)
    .join(', ');
  return [
    row.employee,
    row.department,
    formatAttendanceDay(row.date),
    r ? formatTimeOfDay(r.StartTimeUtc, false, r.Timezone) : '',
    r && !isOpenRecord(r) ? formatShiftEnd(r.StartTimeUtc, r.EndTimeUtc, false, r.Timezone) : '',
    duration(r?.TotalBreakTimeInSecond),
    duration(r?.TotalWorkTimeInSecond),
    duration(r?.ExpectedWorkingHourInSecond),
    duration(r?.OverTimeInSecond),
    statusText,
  ].map((cell) => (cell === '-' ? '' : cell));
};

const fileName = (scope: string) => `attendance-${scope}-${exportDateStamp()}`;

export const downloadAttendanceCsv = (
  rows: AttendanceExportRow[],
  t: (key: string) => string,
  scope: string
) => {
  const headers = HEADER_KEYS.map((key) => t(key));
  const csvRows = rows.map((row) => {
    const cells = cellsOf(row, t);
    return Object.fromEntries(headers.map((header, index) => [header, cells[index]]));
  });
  downloadCsv(fileName(scope), buildCsv(headers, csvRows));
};

/** A plain landscape A4 table, one line per row, repeating the header on every page. */
export const downloadAttendancePdf = (
  rows: AttendanceExportRow[],
  t: (key: string) => string,
  scope: string,
  title: string
) => {
  const pdf = new jsPDF('l', 'mm', 'a4');
  const widths = [46, 40, 26, 18, 22, 20, 22, 22, 20, 41];
  const left = 10;
  const lineHeight = 6.5;
  const bottom = pdf.internal.pageSize.getHeight() - 10;
  const clip = (text: string, width: number) => {
    let out = text;
    while (out.length > 1 && pdf.getTextWidth(out) > width - 2) out = out.slice(0, -1);
    return out === text ? text : `${out.slice(0, -1)}…`;
  };
  const drawRow = (cells: string[], y: number, bold: boolean) => {
    pdf.setFont('helvetica', bold ? 'bold' : 'normal');
    let x = left;
    cells.forEach((cell, index) => {
      pdf.text(clip(cell, widths[index]), x, y);
      x += widths[index];
    });
  };
  const header = HEADER_KEYS.map((key) => t(key));
  let y = 16;
  pdf.setFontSize(12);
  pdf.text(title, left, y);
  pdf.setFontSize(8);
  y += 8;
  drawRow(header, y, true);
  y += lineHeight;
  for (const row of rows) {
    if (y > bottom) {
      pdf.addPage();
      y = 14;
      drawRow(header, y, true);
      y += lineHeight;
    }
    drawRow(cellsOf(row, t), y, false);
    y += lineHeight;
  }
  pdf.save(`${fileName(scope)}.pdf`);
};
